import AppButton from '@nxsys/components/AppButton';
import { useDrawerSave } from './useDrawerSave';
import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppLoader from '@nxsys/components/AppLoader';
import AppSelect from '@nxsys/components/AppSelect';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { LoanTypesEnum } from '@nxsys/constants/AppEnums';
import { PAYMENT_MODEL_ENUM } from '@nxsys/constants/AppConstants';
import { includes } from 'lodash';
import { useUpdateSettingEmployeeById } from '@nxsys/hooks/api';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { employeeActions } from '@nxsys/store/slices/employee/reducer/employeeSlice';
import { IRELAND } from '@nxsys/store/constants/keys';
import { Icons } from '@nxsys/types/icons';
import { showMessage } from '@nxsys/utils/message';
import Inheritable from '@nxsys/components/Inheritable';
import { Col, Form, Row, Switch, Typography } from 'antd';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { EmployeeRecordType } from '@nxsys/types/models/employee';

type Props = {
  data: EmployeeRecordType | undefined;
  readOnly?: boolean;
};

const SettingsOthers = ({ data, readOnly }: Props) => {
  const { messages } = useTypedIntl();
  const params = useParams();
  const { payrollCountry, isEmployee, user, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );
  const dispatch = useDispatch<AppDispatch>();
  const isIreland = payrollCountry === IRELAND;
  const isCisNonCis = includes(
    [PAYMENT_MODEL_ENUM.CIS, PAYMENT_MODEL_ENUM.NON_CIS],
    data?.setting?.paymentModel,
  );
  const employeeId = isEmployee
    ? user?.subcontractorId?.id
    : (params?.employeeId ?? null);

  const [form] = Form.useForm();
  const watchValues = Form.useWatch([], form);

  const isPayrollSettingLoading = useSelector(
    (state: AppState) => state.employeeStore.employeeSetting.loading,
  );
  const payrollSettingData = useSelector(
    (state: AppState) => state.employeeStore.employeeSetting.data,
  );
  const getPayrollSettingByEmployeeId = useCallback(
    (id: string) => dispatch(employeeActions.getPayrollSettingByEmployeeId(id)),
    [dispatch],
  );
  const getEmployeeById = useCallback(
    (id: string) => dispatch(employeeActions.getEmployeeById(id)),
    [dispatch],
  );

  const { run: updateSettingEmployeeById, loading: isUpdating } =
    useUpdateSettingEmployeeById();

  useEffect(() => {
    if (employeeId) {
      getPayrollSettingByEmployeeId(employeeId);
    }
  }, [employeeId]);

  useEffect(() => {
    if (payrollSettingData) {
      // Find the selected student loan plan (excluding PGL which is separate)
      const planValue = payrollSettingData?.checkedStudentPlans?.find(
        (p: string) => p !== LoanTypesEnum.PGL,
      );
      const initPlanOption = planValue
        ? (studentLoanPlanOptions.find((o) => o.value === planValue) ?? null)
        : null;
      const hasPGL =
        payrollSettingData?.checkedStudentPlans?.includes(LoanTypesEnum.PGL) ??
        false;

      form.setFieldsValue({
        haveStudentLoan: payrollSettingData?.haveStudentLoan ?? false,
        checkedStudentPlans: initPlanOption,
        hasPostGraduateLoan: hasPGL,
        studentLoanPlan1StartDate:
          payrollSettingData?.studentLoanPlan1StartDate ?? '',
        studentLoanPlan1EndDate:
          payrollSettingData?.studentLoanPlan1EndDate ?? '',
        studentLoanPlan2StartDate:
          payrollSettingData?.studentLoanPlan2StartDate ?? '',
        studentLoanPlan2EndDate:
          payrollSettingData?.studentLoanPlan2EndDate ?? '',
        studentLoanPlan4StartDate:
          payrollSettingData?.studentLoanPlan4StartDate ?? '',
        studentLoanPlan4EndDate:
          payrollSettingData?.studentLoanPlan4EndDate ?? '',
        studentLoanPlan5StartDate:
          (payrollSettingData as any)?.studentLoanPlan5StartDate ?? '',
        studentLoanPlan5EndDate:
          (payrollSettingData as any)?.studentLoanPlan5EndDate ?? '',
        studentLoanPostGraStartDate:
          payrollSettingData?.studentLoanPostGraStartDate ?? '',
        studentLoanPostGraEndDate:
          payrollSettingData?.studentLoanPostGraEndDate ?? '',
        isSendEmailSelfBill: payrollSettingData?.isSendEmailSelfBill ?? null,
        isSendEmailP45: payrollSettingData?.isSendEmailP45 ?? null,
        isSendEmailP60: payrollSettingData?.isSendEmailP60 ?? null,
        enableAWRTracking: payrollSettingData?.enableAWRTracking ?? false,
        enableWTR: payrollSettingData?.enableWTR ?? null,
        // NX-5013: agencyConductRegulation lives on the subcontractor entity
        // as a RegulationEnum string (not boolean) and is owned by the
        // General Information tab — single-source-of-truth pattern matching
        // cis-frontend. Removed from SettingsOthers to avoid the
        // wrong-source / wrong-type wipe.
        offPayrollWorker: payrollSettingData?.offPayrollWorker ?? false,
        irregularPaidWorker: payrollSettingData?.irregularPaidWorker ?? false,
        enableBenefits: payrollSettingData?.enableBenefits ?? null,
        outsideUKWorker: data?.pensionSetting?.outsideUKWorker ?? false,
        isApprentice: payrollSettingData?.isApprentice ?? false,
      });
    }
  }, [payrollSettingData]);

  const studentLoanPlanOptions = [
    { label: `${messages['common.plan']} 1`, value: LoanTypesEnum.PLAN_1 },
    { label: `${messages['common.plan']} 2`, value: LoanTypesEnum.PLAN_2 },
    { label: `${messages['common.plan']} 4`, value: LoanTypesEnum.PLAN_4 },
    { label: `${messages['common.plan']} 5`, value: LoanTypesEnum.PLAN_5 },
  ];

  const studentLoanPlanDateFields: Record<
    string,
    { startField: string; endField: string }
  > = {
    [LoanTypesEnum.PLAN_1]: {
      startField: 'studentLoanPlan1StartDate',
      endField: 'studentLoanPlan1EndDate',
    },
    [LoanTypesEnum.PLAN_2]: {
      startField: 'studentLoanPlan2StartDate',
      endField: 'studentLoanPlan2EndDate',
    },
    [LoanTypesEnum.PLAN_4]: {
      startField: 'studentLoanPlan4StartDate',
      endField: 'studentLoanPlan4EndDate',
    },
    [LoanTypesEnum.PLAN_5]: {
      startField: 'studentLoanPlan5StartDate',
      endField: 'studentLoanPlan5EndDate',
    },
  };

  const handleSave = async () => {
    if (!employeeId) return;
    try {
      const values = form.getFieldsValue(true);
      // NX-6215: the parent toggle gates every loan type on the backend, so a
      // payload with loan dates but haveStudentLoan=false deducts nothing.
      // Drop the children here too, not just in the UI.
      const haveStudentLoan = values.haveStudentLoan ?? false;
      const selectedPlan: string | null = haveStudentLoan
        ? (values.checkedStudentPlans?.value ?? null)
        : null;
      const hasPGL = haveStudentLoan && (values.hasPostGraduateLoan ?? false);

      // Build checkedStudentPlans array from dropdown + PGL toggle
      const checkedPlans: string[] = [];
      if (selectedPlan) checkedPlans.push(selectedPlan);
      if (hasPGL) checkedPlans.push(LoanTypesEnum.PGL);

      const getDateIfSelected = (plan: string, field: string) =>
        selectedPlan === plan || (plan === LoanTypesEnum.PGL && hasPGL)
          ? values[field] || null
          : null;

      await updateSettingEmployeeById(employeeId, {
        haveStudentLoan,
        checkedStudentPlans: checkedPlans,
        studentLoanPlan1StartDate: getDateIfSelected(
          LoanTypesEnum.PLAN_1,
          'studentLoanPlan1StartDate',
        ),
        studentLoanPlan1EndDate: getDateIfSelected(
          LoanTypesEnum.PLAN_1,
          'studentLoanPlan1EndDate',
        ),
        studentLoanPlan2StartDate: getDateIfSelected(
          LoanTypesEnum.PLAN_2,
          'studentLoanPlan2StartDate',
        ),
        studentLoanPlan2EndDate: getDateIfSelected(
          LoanTypesEnum.PLAN_2,
          'studentLoanPlan2EndDate',
        ),
        studentLoanPlan4StartDate: getDateIfSelected(
          LoanTypesEnum.PLAN_4,
          'studentLoanPlan4StartDate',
        ),
        studentLoanPlan4EndDate: getDateIfSelected(
          LoanTypesEnum.PLAN_4,
          'studentLoanPlan4EndDate',
        ),
        studentLoanPlan5StartDate: getDateIfSelected(
          LoanTypesEnum.PLAN_5,
          'studentLoanPlan5StartDate',
        ),
        studentLoanPlan5EndDate: getDateIfSelected(
          LoanTypesEnum.PLAN_5,
          'studentLoanPlan5EndDate',
        ),
        studentLoanPostGraStartDate: getDateIfSelected(
          LoanTypesEnum.PGL,
          'studentLoanPostGraStartDate',
        ),
        studentLoanPostGraEndDate: getDateIfSelected(
          LoanTypesEnum.PGL,
          'studentLoanPostGraEndDate',
        ),
        isSendEmailSelfBill: values.isSendEmailSelfBill ?? null,
        isSendEmailP45: values.isSendEmailP45 ?? null,
        isSendEmailP60: values.isSendEmailP60 ?? null,
        enableAWRTracking: values.enableAWRTracking ?? null,
        enableWTR: values.enableWTR ?? null,
        // NX-5013: agencyConductRegulation owned by GeneralInformation tab
        // (subcontractor entity, RegulationEnum string). Don't ship it here.
        offPayrollWorker: values.offPayrollWorker ?? null,
        irregularPaidWorker: values.irregularPaidWorker ?? null,
        enableBenefits: values.enableBenefits ?? null,
        outsideUKWorker: values.outsideUKWorker ?? null,
        isApprentice: values.isApprentice ?? false,
      } as any);
      if (employeeId) {
        getPayrollSettingByEmployeeId(employeeId);
        getEmployeeById(employeeId);
      }
      showMessage({
        type: 'success',
        content: messages['common.updateSuccess'],
      });
    } catch (error: any) {
      if (error?.errorFields) return;
    }
  };

  // NX-4682: in the quick-edit drawer, surface Save in the drawer header.
  const inDrawer = useDrawerSave(handleSave, isUpdating, !readOnly);

  if (!payrollSettingData || isPayrollSettingLoading)
    return (
      <div className="relative h-[100px]">
        <AppLoader />
      </div>
    );

  const renderToggleItem = (
    name: string,
    label: string,
    onChange?: (checked: boolean) => void,
  ) => (
    <Form.Item name={name} valuePropName="checked" className="!mb-4">
      <div className="flex items-center justify-between">
        <span className="text-[16px]">{label}</span>
        <Switch
          checked={watchValues?.[name]}
          onChange={(checked) => {
            form.setFieldValue(name, checked);
            onChange?.(checked);
          }}
        />
      </div>
    </Form.Item>
  );

  // NX-6215: the backend gates ALL loan types behind `haveStudentLoan`
  // (Setting.getStudentLoanTypes returns [] when it is false), so PGL must
  // never be left switched on while the parent toggle is off - that saves
  // cleanly but silently deducts nothing. Mirrors the legacy portals.
  const clearStudentLoanFields = () => {
    form.setFieldsValue({
      checkedStudentPlans: null,
      hasPostGraduateLoan: false,
      studentLoanPlan1StartDate: '',
      studentLoanPlan1EndDate: '',
      studentLoanPlan2StartDate: '',
      studentLoanPlan2EndDate: '',
      studentLoanPlan4StartDate: '',
      studentLoanPlan4EndDate: '',
      studentLoanPlan5StartDate: '',
      studentLoanPlan5EndDate: '',
      studentLoanPostGraStartDate: '',
      studentLoanPostGraEndDate: '',
    } as any);
  };

  const renderInheritableToggle = (name: string, label: string) => (
    <Form.Item name={name} className="!mb-4" wrapperCol={{ span: 24 }}>
      <Inheritable<boolean>
        label={label}
        name={name}
        value={watchValues?.[name]}
        inheritedValue={false}
        form={form}
        disabled={readOnly}
      >
        {({ value, onChange, disabled }) => (
          <Switch
            checked={value ?? false}
            disabled={disabled}
            onChange={onChange}
          />
        )}
      </Inheritable>
    </Form.Item>
  );

  const renderSelectedPlanDateFields = () => {
    const selectedPlan = watchValues?.checkedStudentPlans?.value ?? null;
    if (!selectedPlan) return null;

    const fields = studentLoanPlanDateFields[selectedPlan];
    if (!fields) return null;

    return (
      <Row gutter={24} className="mt-2">
        <Col xs={24} sm={12}>
          <Form.Item
            label={messages['common.startDate']}
            name={fields.startField}
            rules={[{ required: true, message: messages['form.pleaseSelect'] }]}
          >
            <AppDatePicker
              className="w-full !min-w-0"
              placeholder={messages['form.selectDate']}
              format="DD/MM/YYYY"
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item label={messages['common.endDate']} name={fields.endField}>
            <AppDatePicker
              className="w-full !min-w-0"
              placeholder={messages['form.selectDate']}
              format="DD/MM/YYYY"
            />
          </Form.Item>
        </Col>
      </Row>
    );
  };

  return (
    <div className="relative">
      {isUpdating && (
        <div className="absolute inset-0 z-10 bg-white/60">
          <AppLoader />
        </div>
      )}
      <Typography.Title
        level={5}
        className="!mb-8 !text-[20px] !leading-[1.6] !tracking-[-0.02em] !text-[#1A1D1F]"
      >
        {messages['common.other'] ?? 'Others'}
      </Typography.Title>

      <AppFormContainer
        form={form}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
        disabled={readOnly}
      >
        {/* DEDUCT STUDENT LOAN - only for non-CIS/Non-CIS */}
        {!isIreland && !isCisNonCis && (
          <>
            {renderToggleItem(
              'haveStudentLoan',
              messages['common.deductionStudentLoan'] ?? 'Deduct Student Loan',
              (checked) => {
                if (!checked) clearStudentLoanFields();
              },
            )}

            {watchValues?.haveStudentLoan && (
              <>
                <div className="ml-0 mb-4">
                  <Form.Item
                    name="checkedStudentPlans"
                    label={
                      messages['common.selectPlan'] ??
                      'Select Student Loan Plan'
                    }
                  >
                    <AppSelect
                      options={studentLoanPlanOptions}
                      placeholder={
                        messages['common.selectPlan'] ??
                        'Select Student Loan Plan'
                      }
                      allowCapitalize={false}
                    />
                  </Form.Item>
                  {renderSelectedPlanDateFields()}
                </div>

                {/* Postgraduate Loan - sub-option of Deduct Student Loan */}
                <div className="ml-0">
                  <Form.Item
                    name="hasPostGraduateLoan"
                    valuePropName="checked"
                    className="!mb-4"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-[16px]">
                        {messages[
                          'common.postGraduateStudentLoanEnglishAndWales'
                        ] ?? 'Postgraduate Student Loan (England & Wales)'}
                      </span>
                      <Switch
                        checked={watchValues?.hasPostGraduateLoan}
                        onChange={(checked) =>
                          form.setFieldValue('hasPostGraduateLoan', checked)
                        }
                      />
                    </div>
                  </Form.Item>
                  {watchValues?.hasPostGraduateLoan && (
                    <Row gutter={24} className="mt-2">
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={messages['common.startDate']}
                          name="studentLoanPostGraStartDate"
                          rules={[
                            {
                              required: true,
                              message: messages['form.pleaseSelect'],
                            },
                          ]}
                        >
                          <AppDatePicker
                            className="w-full !min-w-0"
                            placeholder={messages['form.selectDate']}
                            format="DD/MM/YYYY"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label={messages['common.endDate']}
                          name="studentLoanPostGraEndDate"
                        >
                          <AppDatePicker
                            className="w-full !min-w-0"
                            placeholder={messages['form.selectDate']}
                            format="DD/MM/YYYY"
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  )}
                </div>
              </>
            )}

            {!isEmployee && (
              <div className="h-[8px] w-full rounded-[4px] bg-[#EFEFEF] my-6" />
            )}
          </>
        )}

        {/* COMMUNICATION & COMPLIANCE - hidden for employee portal */}
        {!isIreland &&
          !isCisNonCis &&
          renderToggleItem(
            'isApprentice',
            messages['common.isApprentice'] ?? 'Is Apprentice',
          )}

        <div className="h-px bg-neutral-200 my-4" />

        {!isEmployee && (
          <>
            <Typography.Text className="!text-[14px] !font-semibold !text-[#6F767E] !uppercase !tracking-wider">
              {messages['common.communicationAndCompliance'] ??
                'COMMUNICATION & ComPliance'}
            </Typography.Text>
            <div className="mt-4">
              {isCisNonCis ? (
                <>
                  {/* CIS/Non-CIS: simplified communication section */}
                  {!isBureau &&
                    renderToggleItem(
                      'enableAWRTracking',
                      messages['common.enableAWRTracking'] ??
                        'Enable AWR Tracking',
                    )}
                  {renderInheritableToggle(
                    'enableWTR',
                    messages['common.enableWorkingTimeRegulationTracking'] ??
                      'Enable Working Time Regulation Tracking',
                  )}
                  {renderInheritableToggle(
                    'enableBenefits',
                    messages['common.enableBenefits'] ?? 'Enable Benefits',
                  )}
                  {/* NX-5013: agencyConductRegulation moved to General Information tab — single source of truth (matches cis-frontend). */}
                  {renderInheritableToggle(
                    'isSendEmailSelfBill',
                    messages['common.emailSelfBill'] ?? 'Email Self Bill?',
                  )}
                </>
              ) : (
                <>
                  {/* Umbrella/PAYEE: full communication section */}
                  {renderInheritableToggle(
                    'isSendEmailSelfBill',
                    messages['common.emailPayslip'] ?? 'Email Payslip?',
                  )}

                  {!isIreland && (
                    <Row gutter={24}>
                      <Col xs={24} sm={12}>
                        {renderInheritableToggle(
                          'isSendEmailP45',
                          messages['common.emailP45'] ?? 'Email P45?',
                        )}
                      </Col>
                      <Col xs={24} sm={12}>
                        {renderInheritableToggle(
                          'isSendEmailP60',
                          messages['common.emailP60'] ?? 'Email P60?',
                        )}
                      </Col>
                    </Row>
                  )}

                  {!isIreland && (
                    <>
                      {!isBureau &&
                        renderToggleItem(
                          'enableAWRTracking',
                          messages['common.enableAWRTracking'] ??
                            'Enable AWR Tracking',
                        )}
                      {renderInheritableToggle(
                        'enableWTR',
                        messages[
                          'common.enableWorkingTimeRegulationTracking'
                        ] ?? 'Enable Working Time Regulation Tracking',
                      )}
                      {/* NX-5013: agencyConductRegulation moved to General Information tab — single source of truth (matches cis-frontend). */}

                      <Row gutter={24}>
                        <Col xs={24} sm={12}>
                          {renderToggleItem(
                            'offPayrollWorker',
                            messages['common.offPayrollWorker'] ??
                              'Off payroll worker',
                          )}
                        </Col>
                        <Col xs={24} sm={12}>
                          {renderToggleItem(
                            'irregularPaidWorker',
                            messages['common.irregularPaidWorker'] ??
                              'Irregular Paid Worker',
                          )}
                        </Col>
                      </Row>

                      <Row gutter={24}>
                        <Col xs={24} sm={12}>
                          {renderInheritableToggle(
                            'enableBenefits',
                            messages['common.enableBenefits'] ??
                              'Enable Benefits',
                          )}
                        </Col>
                        <Col xs={24} sm={12}>
                          {renderToggleItem(
                            'outsideUKWorker',
                            messages['common.outsideUKWorker'] ??
                              'Outside UK Worker',
                          )}
                        </Col>
                      </Row>
                    </>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </AppFormContainer>

      {/* Action Buttons */}
      {!readOnly && !inDrawer && (
        <div className="flex items-center justify-start gap-3 pt-6">
          <AppButton
            type="primary"
            name={messages['common.save']}
            leftIcon={
              <Icons.IconCheckCircle color={defaultTheme.theme.palette.white} />
            }
            onClick={handleSave}
            loading={isUpdating}
          />
        </div>
      )}
    </div>
  );
};

export default SettingsOthers;
